import {
  isSelectionFormElementType,
  isTextLikeFormElementType,
} from '@/common/utils/liveFormFieldOverrides'
import {
  isListboxEmptyValue,
  listboxFieldValuesEqual,
} from '@/common/utils/resolveListboxFieldValue'

/** PrimeVue Select @change / @option-select. Keep boolean false (`??`, not `||`). */
export function resolvePrimeSelectEventValue(event: unknown): unknown {
  if (event != null && typeof event === 'object' && !Array.isArray(event) && 'value' in event) {
    return (event as { value: unknown }).value
  }
  return event
}

export function shouldSkipDuplicateSelectionOnChange(options: {
  value: unknown
  lastValue: unknown
  lastAt: number
  now?: number
  windowMs?: number
}): boolean {
  const now = options.now ?? Date.now()
  const windowMs = options.windowMs ?? 100
  return options.value === options.lastValue && now - options.lastAt < windowMs
}

/**
 * PrimeVue may re-emit the current ListBox value (blur, same option, parent re-render).
 * Compare against the prop still holding the pre-update value. Objects/arrays stay
 * reference-only so returnObject / multiselect changes are not collapsed via String().
 */
export function isRepeatedListboxSelection(next: unknown, current: unknown): boolean {
  if (Object.is(next, current)) return true
  if (Array.isArray(next) || Array.isArray(current)) return false
  if (
    (typeof next === 'object' && next != null) ||
    (typeof current === 'object' && current != null)
  ) {
    return false
  }
  return listboxFieldValuesEqual(next, current)
}

/** User changed an already-selected ListBox (not autoSelectFirst / empty mount). */
export function shouldRunSelectionOnChangeAfterInput(options: {
  previousValue: unknown
  nextValue: unknown
}): boolean {
  if (isListboxEmptyValue(options.previousValue)) return false
  return !listboxFieldValuesEqual(options.previousValue, options.nextValue)
}

export function shouldDeferOnChangeToHandleChange(
  inlineOnChangeScript: string,
  elementType: string | undefined | null,
): boolean {
  return inlineOnChangeScript.trim() !== '' && isSelectionFormElementType(elementType)
}

export function shouldTreatOnChangeAsAlreadyRanViaInput(options: {
  eventName: string
  inlineOnChangeScript: string
  elementType: string | undefined | null
  isEditableTableOnChange: boolean
}): boolean {
  const { eventName, inlineOnChangeScript, elementType, isEditableTableOnChange } = options
  if (eventName !== 'onChange' || inlineOnChangeScript.trim() === '') {
    return false
  }
  if (isTextLikeFormElementType(elementType)) {
    return false
  }
  if (isEditableTableOnChange) {
    return false
  }
  if (shouldDeferOnChangeToHandleChange(inlineOnChangeScript, elementType)) {
    return false
  }
  // DATE/DATETIME: onChange уже из _input → onElement; Path B (runAction) не дублировать.
  return true
}
